/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20260611 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { CellphonesDaoService } from '../cellphones-dao.service';
import { Cellphone } from 'src/app/_model/cellphone';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { CellphoneDbTableDefinition } from './cellphone-db-table-definition';

@Injectable()
export class CellphonesDbDaoService implements CellphonesDaoService {
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private cellphoneTableDefinition: CellphoneDbTableDefinition,
  ) {}

  public async saveCellphones(cellphones: Cellphone[]): Promise<void> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      const queries: Query[] = cellphones.map((cellphone) => ({
        statement:
          'INSERT OR REPLACE INTO ' +
          this.cellphoneTableDefinition.tableName +
          '(id, label, phonenumber, phonenumber_cleartext, name, imei, type, brand, typename,' +
          ' employeeid, employeelabel, mobileclientid, mobileclientlabel, databag)' +
          ' VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?)',
        parameters: [
          Number(cellphone.getImsId()),
          cellphone.label,
          cellphone.phonenumber,
          cellphone.phonenumberCleartext,
          cellphone.name,
          cellphone.imei,
          cellphone.type,
          cellphone.brand,
          cellphone.typename,
          cellphone.employee ? Number(cellphone.employee.getImsId()) : undefined,
          cellphone.employee?.label,
          cellphone.mobileclient ? Number(cellphone.mobileclient.getImsId()) : undefined,
          cellphone.mobileclient?.label,
          cellphone.databag ? JSON.stringify(cellphone.databag) : undefined,
        ],
      }));
      await this.dbPersistenceService.executeBatch(db, queries);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error saving cellphones to db',
        error?.message,
      );
    }
  }

  public async getCellphones(): Promise<Cellphone[]> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      const data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.cellphoneTableDefinition.tableName + ';',
        parameters: null,
      });
      const cellphones: Cellphone[] = [];
      for (let i = 0; i < data.values.length; i++) {
        const row = data.values[i];
        cellphones.push(
          Cellphone.fromDto(
            {
              id: row['id'],
              label: row['label'],
              phonenumber: row['phonenumber'],
              phonenumber_cleartext: row['phonenumber_cleartext'],
              name: row['name'],
              imei: row['imei'],
              type: row['type'],
              brand: row['brand'],
              typename: row['typename'],
              employee: row['employeeid']
                ? { id: String(row['employeeid']), label: row['employeelabel'] }
                : undefined,
              mobileclient: row['mobileclientid']
                ? { id: String(row['mobileclientid']), label: row['mobileclientlabel'] }
                : undefined,
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
          ),
        );
      }
      return cellphones;
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error reading cellphones from db',
        error?.message,
      );
      return [];
    }
  }

  public async reset(): Promise<void> {
    // Do nothing
  }

  public async clear(): Promise<void> {
    const db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.cellphoneTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.cellphoneTableDefinition.tableName,
    );
  }
}
